/**
 * Avatars render tiny (20-40 CSS px) but `customAvatar.value` / `customAvatar.cdnValue` point at the
 * full-resolution upload — typically ~1200px, and up to the original camera file when `compressFile`
 * times out and falls back. Handing a browser a 1200px bitmap for a 34px box makes it downscale by
 * ~36x in one step. Chrome does that in high-quality multiple steps; Safari uses a cheaper filter, so
 * the same `<img>` looks blurry there and sharp in Chrome (#22459).
 *
 * For Cloudinary delivery URLs we can ask the CDN for a face-cropped thumbnail sized to how the avatar
 * actually renders. S3/other hosts don't support URL-based resizing, so those URLs are returned
 * untouched — a browser downscale is all we have there. URLs that already carry a transformation
 * segment are left alone so transforms never stack.
 *
 * Keeping the delivered bitmap small is also load-bearing for memory: full-resolution avatars in long
 * lists were a driver of the low-memory iPad OOM crash fixed by #21020 / #21034. Do not widen the
 * requested size to "look safer".
 */
const CLOUDINARY_UPLOAD_MARKER = '/image/upload/'

/** Cloudinary bills a variant per dpr value, and past 3x the extra pixels are not visible. */
const MIN_DPR = 1
const MAX_DPR = 3

/**
 * Historical default. `EstimatorAvatar` has hard-coded 2 since #21034, and non-browser callers
 * (SSR, node tests) have no `window` to read.
 */
const FALLBACK_DPR = 2

/**
 * Screen pixel density to request, clamped to a range Cloudinary is worth billing for.
 *
 * Reading `window.devicePixelRatio` matters both ways: a 3x display served a 2x bitmap stretches it
 * and looks soft, and a 1x display served a 2x bitmap pays for double the bytes it can show.
 */
export function getAvatarDpr(): number {
  if (typeof window === 'undefined') return FALLBACK_DPR

  const ratio = window.devicePixelRatio
  if (typeof ratio !== 'number' || !isFinite(ratio) || ratio <= 0) return FALLBACK_DPR

  const clamped = Math.min(Math.max(ratio, MIN_DPR), MAX_DPR)
  // one decimal place keeps the URL (and so the Cloudinary cache key) stable across odd ratios
  return Math.round(clamped * 10) / 10
}

/**
 * Cloudinary reads `dpr_2` and `dpr_2.0` the same way, but they are different URLs, so they are
 * different CDN cache entries. `EstimatorAvatar` has shipped `dpr_2.0` since #21034 across 30+ call
 * sites. Keeping one decimal place on whole numbers means this extraction reuses every warm entry
 * instead of re-rendering all of them on deploy.
 */
function formatDpr(dpr: number): string {
  return Number.isInteger(dpr) ? dpr.toFixed(1) : String(dpr)
}

export interface GetAvatarThumbUrlOptions {
  /**
   * Pixel density to request. Defaults to the device's, clamped to 1-3. Pass a fixed value to keep a
   * caller's delivered URLs stable regardless of display.
   */
  dpr?: number
}

/**
 * Rewrite a Cloudinary avatar URL to deliver a face-cropped thumbnail at `size` CSS px.
 *
 * Returns the input unchanged when it is empty, is not a Cloudinary delivery URL, or already carries
 * a transformation segment.
 *
 * @param url the stored avatar URL (`customAvatar.value` / `cdnValue`)
 * @param size the rendered size in CSS pixels — pass what the element actually shows, not a margin
 */
export function getAvatarThumbUrl(url: string, size: number, options: GetAvatarThumbUrlOptions = {}): string {
  if (!url) return url

  const markerIndex = url.indexOf(CLOUDINARY_UPLOAD_MARKER)
  if (markerIndex === -1) return url

  const insertAt = markerIndex + CLOUDINARY_UPLOAD_MARKER.length
  const afterMarker = url.slice(insertAt)
  // already transformed (segment looks like `c_fill,...` / `w_64/`) — don't stack another transform
  if (/^[a-z]{1,3}_[^/]+\//.test(afterMarker)) return url

  const dpr = options.dpr ?? getAvatarDpr()
  const transform = `c_fill,g_face,w_${size},h_${size},dpr_${formatDpr(dpr)},f_auto,q_auto/`

  return url.slice(0, insertAt) + transform + afterMarker
}

/**
 * Read a pixel size out of the loosest thing a caller might have — a `width`/`height` prop that could
 * be `36`, `'36px'`, or missing. Returns the first positive value, else `fallback`.
 *
 * This cannot see a size set purely in CSS (`className` / `classes`). Those callers must pass an
 * explicit number.
 */
export function parseAvatarSize(values: Array<string | number | undefined>, fallback = 28): number {
  for (const value of values) {
    const parsed = parseInt(String(value ?? ''), 10)
    if (parsed > 0) return parsed
  }
  return fallback
}
